import React, { useEffect } from 'react';
import useGetPage from '../../api/useGetPage';
import { useQuery } from 'react-query';
import DisplayQueryError from '../shared/DisplayQueryError';
import LoadingSpinner from '../shared/LoadingSpinner';
import HtmlComponent from '../shared/HtmlComponent';
import { VideoType } from '../../types';
import getCurrentUrl from '../../lib/getCurrentUrl';
import { Link } from 'react-router-dom';
import { createUrlFromQuestion } from '../knowledge/createUrlFromQuestion';
import RelatedQuestionsAndArticles from '../shared/RelatedQuestionsAndArticles';
import DocumentHead from '../shared/DocumentHead';
import Header from '../shared/Header';
import FeedbackBtn from '../shared/FeedbackBtn';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { onGiveFeedback } from '../../lib/feedback';
import useTrackPageView from '../../hooks/useTrackPageView.ts';
import usePagesUserId from '../../hooks/usePagesUserId.ts';

interface AnswerPageProps {
    questionId: number;
    slug: string;
}

const AnswerPage: React.FC<AnswerPageProps> = ({ questionId, slug }) => {
    const questionText = slug.split('-').join(' ').split('!~~!').join('-');

    const pagesUserId = usePagesUserId();
    useTrackPageView(questionId);

    const getPage = useGetPage({
        questionId,
        questionText
    });

    const {
        data: page,
        isLoading,
        error,
        refetch
    } = useQuery(['page', questionId, questionText], getPage);

    useEffect(() => {
        if (page && page.isForm) {
            if (window.faqbot) {
                window.faqbot.open();
                window.faqbot.askQuestion(page.question);
            }
        }
    }, [page]);

    if (error) {
        return <DisplayQueryError error={error} refetch={refetch} />;
    }

    if (isLoading || !page) {
        return <LoadingSpinner />;
    }

    if (page.isForm) {
        return (
            <>
                <DocumentHead title={' - ' + page.question} />
                <Header headerClassName='mini' />
                <section className='white-bg py-5'>
                    <Container>
                        <Row>
                            <Col xs={12}>
                                <Card className='question-card limited-length-card'>
                                    <CardBody>
                                        <h3 className='card-title'>
                                            Opening chatbot...
                                        </h3>
                                        <div className='card-text mt-3'>
                                            <p>
                                                ...after a few seconds you can
                                                type in your details there
                                            </p>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }

    return (
        <>
            <DocumentHead title={' - ' + page.question} />
            <Header headerClassName='mini' />
            <section className='white-bg pt-5 pb-4'>
                <Container>
                    <Row>
                        <Col xs={12}>
                            <Card className='custom-card answer-card limited-length-card mt-3'>
                                <CardBody className='custom-card-body'>
                                    <div className='card-title-holder d-flex justify-content-between align-items-center'>
                                        <h1 className='card-title pr-3'>
                                            {page.question}
                                        </h1>
                                    </div>
                                    <div className='card-text mt-3'>
                                        <HtmlComponent
                                            html={page.answer.text}
                                        />
                                        {page.video?.type ===
                                            VideoType.YouTube && (
                                            <div className='embed-responsive embed-responsive-16by9 mb-3'>
                                                <iframe
                                                    id='player'
                                                    width='600'
                                                    height='400'
                                                    src={`${
                                                        page.video.url
                                                    }?enablejsapi=1&origin=${encodeURIComponent(
                                                        getCurrentUrl()
                                                    )}`}
                                                    frameBorder='0'
                                                ></iframe>
                                            </div>
                                        )}
                                        {page.video &&
                                            page.video.type !==
                                                VideoType.YouTube && (
                                                <a
                                                    title={page.video.url}
                                                    href={page.video.url}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    Check out this video 📺
                                                </a>
                                            )}
                                    </div>
                                    {page.followUps?.length > 0 && (
                                        <div className='card-footer mt-3'>
                                            <div className='card-footer-content d-flex'>
                                                <div className='follow-ups'>
                                                    <h6 className='font-weight-bold'>
                                                        Further Questions:
                                                    </h6>
                                                    {page.followUps.map(
                                                        (followUp) => (
                                                            <Link
                                                                key={
                                                                    followUp.questionId
                                                                }
                                                                className='btn btn-primary mb-1'
                                                                target='_self'
                                                                to={createUrlFromQuestion(
                                                                    followUp.questionId,
                                                                    followUp.text
                                                                )}
                                                            >
                                                                {followUp.text}
                                                            </Link>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <hr />
                                    <Row>
                                        <Col xs={12}>
                                            <div className='align-self-start'>
                                                {page.tags.map((tag) => (
                                                    <Link
                                                        key={tag.text}
                                                        className='btn btn-outline-primary btn-pill m-1 px-2 py-1'
                                                        role='link'
                                                        to={`/topic/${encodeURIComponent(
                                                            tag.text
                                                        )}`}
                                                    >
                                                        <small>
                                                            {tag.text}
                                                        </small>
                                                    </Link>
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className='d-flex flex-column justify-content-center align-items-center mt-3'>
                                        <h5>Helpful?</h5>
                                        <div>
                                            <FeedbackBtn
                                                sessionKey={`${page.kbEntryId}-interaction`}
                                                good
                                                onClick={() =>
                                                    onGiveFeedback(
                                                        pagesUserId,
                                                        page.kbEntryId,
                                                        1,
                                                        100,
                                                        page.question.replace(
                                                            "'",
                                                            '&apos;'
                                                        )
                                                    )
                                                }
                                            />
                                            <FeedbackBtn
                                                sessionKey={`${page.kbEntryId}-interaction`}
                                                onClick={() =>
                                                    onGiveFeedback(
                                                        pagesUserId,
                                                        page.kbEntryId,
                                                        0,
                                                        100,
                                                        page.question.replace(
                                                            "'",
                                                            '&apos;'
                                                        )
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
            <RelatedQuestionsAndArticles
                qnaEntityId={page.qnaEntityId}
                tags={page.tags}
            />
        </>
    );
};

export default AnswerPage;
